import type { $Enums } from '@prisma/client';
import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';

/**
 * SHIPMENTS OWNED BY A BUY-SELL ORDER OR AN INVENTORY TRANSFER.
 *
 * Sales order, purchase order and grower product shipments are judged by their owner's header door
 * (`salesOrderPolicy`, `purchaseOrderPolicy`, `growerProductPolicy`); this policy covers the two owners
 * with none, and copies their owner form's behavior today:
 *
 *   owner             fields the owner form edits                         window
 *   buy-sell order    terms · freight company · ship / delivery date ·    DRAFT · SUBMITTED ·
 *                     temperatures · origin · destination                 PAYMENT_RECONCILIATION
 *   inventory xfer    ship / delivery date · temperatures · origin ·      DRAFT · SUBMITTED
 *                     destination
 *   either            custom fields                                       every status (blockEditing only)
 *   either            driver / arrival / place logistics, and any field   ungoverned: the owner form
 *                     its form does not show                              never writes them
 */
export const SHIPMENT_FIELDS = [
  'terms',
  'freightCompany',
  'shipDate',
  'deliveryDate',
  'temperature',
  'origin',
  'destination',
  'logistics',
  'customFields',
] as const;

export type ShipmentField = (typeof SHIPMENT_FIELDS)[number];

export type ShipmentFacts =
  | { owner: 'buySellOrder'; ownerStatus: $Enums.BuySellOrderStatus }
  | { owner: 'inventoryTransfer'; ownerStatus: $Enums.InventoryTransferStatus };

/** `BuySellOrderValidator.isFieldEditable` `toPaymentReconciliation` — the shipping form's window (ShippingForm.tsx). */
export const SHIPMENT_BUY_SELL_ORDER_EDITABLE_STATUSES: readonly $Enums.BuySellOrderStatus[] = [
  'DRAFT',
  'SUBMITTED',
  'PAYMENT_RECONCILIATION',
];

/** The transfer page's `canEdit` — every shipping input on InventoryTransferLoadedPage.tsx. */
export const SHIPMENT_INVENTORY_TRANSFER_EDITABLE_STATUSES: readonly $Enums.InventoryTransferStatus[] = [
  'DRAFT',
  'SUBMITTED',
];

const BUY_SELL_ORDER_FORM_FIELDS: readonly ShipmentField[] = [
  'terms',
  'freightCompany',
  'shipDate',
  'deliveryDate',
  'temperature',
  'origin',
  'destination',
];

const INVENTORY_TRANSFER_FORM_FIELDS: readonly ShipmentField[] = [
  'shipDate',
  'deliveryDate',
  'temperature',
  'origin',
  'destination',
];

const decideField = (facts: ShipmentFacts, field: ShipmentField): Decision => {
  switch (facts.owner) {
    case 'buySellOrder':
      return !BUY_SELL_ORDER_FORM_FIELDS.includes(field) ||
        SHIPMENT_BUY_SELL_ORDER_EDITABLE_STATUSES.includes(facts.ownerStatus)
        ? allow()
        : deny(
            'SHIPMENT_BUY_SELL_ORDER_FIELD_LOCKED',
            `Shipping is locked once the buy-sell order is ${getStatusLabel(facts.ownerStatus)}.`
          );
    case 'inventoryTransfer':
      return !INVENTORY_TRANSFER_FORM_FIELDS.includes(field) ||
        SHIPMENT_INVENTORY_TRANSFER_EDITABLE_STATUSES.includes(facts.ownerStatus)
        ? allow()
        : deny(
            'SHIPMENT_INVENTORY_TRANSFER_FIELD_LOCKED',
            `Shipping is locked once the inventory transfer is ${getStatusLabel(facts.ownerStatus)}.`
          );
  }
};

const fieldRule = (facts: ShipmentFacts, action: Action<ShipmentField>): Decision =>
  action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow();

const fieldRules = {
  terms: fieldRule,
  freightCompany: fieldRule,
  shipDate: fieldRule,
  deliveryDate: fieldRule,
  temperature: fieldRule,
  origin: fieldRule,
  destination: fieldRule,
  logistics: fieldRule,
  customFields: fieldRule,
} satisfies Record<ShipmentField, typeof fieldRule>;

export const shipmentPolicy: Policy<ShipmentFacts, ShipmentField> = definePolicy<ShipmentFacts, ShipmentField>({
  kind: 'shipment',
  fields: SHIPMENT_FIELDS,
  ops: [],
  requires: {},
  entity: () => allow(),
  field: fieldRules,
});
